/** Shared AI auto-report widget progress breakdown (list + detail). */

import dayjs from "dayjs";
import { resolveWelcomeWidgetMinReviewCount } from "constants/welcomeWidgetAnalysis";
import {
  welcomeJobStatusDescriptionMeansEmptyGenaiOutput,
  welcomeJobStatusDescriptionMeansInsufficientData,
} from "pages/Welcome/welcomeWidgetDataState";

export function reportDisplayStatus(report) {
  return String(report?.display_status || report?.status || "")
    .trim()
    .toLowerCase();
}

export function reportWidgetBreakdown(report) {
  const notEnoughData =
    (Number(report?.widgets_not_enough_data) || 0) +
    (Number(report?.widgets_empty_genai_signal) || 0);
  return {
    ready: Number(report?.widgets_ready) || 0,
    missing: Number(report?.widgets_missing) || 0,
    failed: Number(report?.widgets_failed) || 0,
    notEnoughData,
    inProgress: Number(report?.widgets_in_progress) || 0,
    total: Number(report?.widgets_total) || 0,
  };
}

/** Map one widget row to a breakdown chip category for scroll targeting. */
export function classifyReportWidgetBreakdown(widget) {
  if (!widget?.id) return "missing";
  const st = Number(widget.status);
  if (st === 1 || st === 2) return "inProgress";
  if (
    widget.job_insufficient_data_signal ||
    welcomeJobStatusDescriptionMeansInsufficientData(widget.job_status_description)
  ) {
    return "notEnoughData";
  }
  if (
    widget.job_empty_genai_signal ||
    welcomeJobStatusDescriptionMeansEmptyGenaiOutput(widget.job_status_description)
  ) {
    return "notEnoughData";
  }
  if (st === 4) return "failed";
  if (st === 3) {
    const rc = Number(widget.review_count_total);
    const minRequired = resolveWelcomeWidgetMinReviewCount(widget.metric_id);
    if (Number.isFinite(rc) && rc < minRequired) {
      return "notEnoughData";
    }
    return "ready";
  }
  return "missing";
}

export function findReportWidgetsForBreakdownCategory(widgets, category) {
  if (!Array.isArray(widgets) || !category) return [];
  return widgets.filter(
    (widget) => classifyReportWidgetBreakdown(widget) === category,
  );
}

export function formatWidgetBreakdownLabel(report, t) {
  const b = reportWidgetBreakdown(report);
  const parts = [];
  if (b.ready > 0) {
    parts.push(t("ai_services.widgets_ready_count", { count: b.ready }));
  }
  if (b.missing > 0) {
    parts.push(t("ai_services.widgets_missing_count", { count: b.missing }));
  }
  if (b.failed > 0) {
    parts.push(t("ai_services.widgets_failed_count", { count: b.failed }));
  }
  if (b.notEnoughData > 0) {
    parts.push(t("ai_services.widgets_not_enough_data_count", { count: b.notEnoughData }));
  }
  if (b.inProgress > 0) {
    parts.push(t("ai_services.widgets_in_progress_count", { count: b.inProgress }));
  }
  if (parts.length === 0 && b.total > 0) {
    return t("ai_services.widgets_progress", { ready: b.ready, total: b.total });
  }
  return parts.join(" · ");
}

export function reportHasActiveWidgetJobs(report) {
  return reportWidgetBreakdown(report).inProgress > 0;
}

/** Colored chip items for report detail header ({ key, label, tone }). */
export function widgetBreakdownChipItems(report, t) {
  const b = reportWidgetBreakdown(report);
  const items = [];
  if (b.ready > 0) {
    items.push({
      key: "ready",
      label: t("ai_services.widgets_ready_count", { count: b.ready }),
      tone: "ready",
    });
  }
  if (b.missing > 0) {
    items.push({
      key: "missing",
      label: t("ai_services.widgets_missing_count", { count: b.missing }),
      tone: "missing",
    });
  }
  if (b.failed > 0) {
    items.push({
      key: "failed",
      label: t("ai_services.widgets_failed_count", { count: b.failed }),
      tone: "failed",
    });
  }
  if (b.notEnoughData > 0) {
    items.push({
      key: "notEnoughData",
      label: t("ai_services.widgets_not_enough_data_count", { count: b.notEnoughData }),
      tone: "notEnoughData",
    });
  }
  if (b.inProgress > 0) {
    items.push({
      key: "inProgress",
      label: t("ai_services.widgets_in_progress_count", { count: b.inProgress }),
      tone: "inProgress",
    });
  }
  return items;
}

export function isOneTimeAiReport(report) {
  return Boolean(report?.data_since && report?.data_until);
}

/** Actual feedback window for widgets/PDF (data_since/until or API coverage fields). */
export function formatAiReportCoverageLabel(report) {
  const sinceRaw = report?.data_since || report?.coverage_since;
  const untilRaw = report?.data_until || report?.coverage_until;
  if (sinceRaw && untilRaw) {
    const since = dayjs(sinceRaw);
    const until = dayjs(untilRaw);
    if (since.isValid() && until.isValid()) {
      return `${since.format("DD MMM YYYY")} – ${until.format("DD MMM YYYY")}`;
    }
  }
  if (!report?.report_month) return "—";
  const m = dayjs(report.report_month);
  return `${m.startOf("month").format("DD MMM YYYY")} – ${m.endOf("month").format("DD MMM YYYY")}`;
}

/** Card/list period title: month label or custom date span for one-time reports. */
export function formatAiReportPeriodLabel(report) {
  if (isOneTimeAiReport(report)) {
    const since = dayjs(report.data_since);
    const until = dayjs(report.data_until);
    if (since.isValid() && until.isValid()) {
      return `${since.format("D MMM YYYY")} – ${until.format("D MMM YYYY")}`;
    }
  }
  if (!report?.report_month) return "—";
  return dayjs(report.report_month).format("MMMM YYYY");
}

/** Per-metric status from list/detail API (`metric_statuses`). */
export function reportMetricStatus(report, metricId) {
  const map = report?.metric_statuses;
  if (!map || typeof map !== "object") return null;
  const key = String(metricId);
  return map[key] || map[metricId] || null;
}

/** Chip icon category: ready | problem | inProgress | null */
export function reportMetricChipTone(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "ready") return "ready";
  if (s === "in_progress") return "inProgress";
  if (
    s === "failed" ||
    s === "missing" ||
    s === "not_enough_data" ||
    s === "notenoughdata"
  ) {
    return "problem";
  }
  return null;
}

const INCOMPLETE_METRIC_STATUSES = new Set([
  "failed",
  "missing",
  "not_enough_data",
  "notenoughdata",
]);

/**
 * Optimistic UI: flip incomplete metric chips to in_progress (e.g. after repair).
 * Ready metrics are unchanged.
 */
export function withIncompleteMetricsInProgress(report) {
  if (!report) return report;
  const map = report.metric_statuses;
  if (!map || typeof map !== "object") return report;
  const nextStatuses = { ...map };
  let flipped = 0;
  Object.keys(nextStatuses).forEach((key) => {
    const s = String(nextStatuses[key] || "")
      .trim()
      .toLowerCase();
    if (INCOMPLETE_METRIC_STATUSES.has(s)) {
      nextStatuses[key] = "in_progress";
      flipped += 1;
    }
  });
  if (flipped <= 0) return report;

  let ready = 0;
  let inProgress = 0;
  let missing = 0;
  let failed = 0;
  let notEnough = 0;
  Object.values(nextStatuses).forEach((raw) => {
    const s = String(raw || "")
      .trim()
      .toLowerCase();
    if (s === "ready") ready += 1;
    else if (s === "in_progress") inProgress += 1;
    else if (s === "missing") missing += 1;
    else if (s === "failed") failed += 1;
    else if (s === "not_enough_data" || s === "notenoughdata") notEnough += 1;
  });

  return {
    ...report,
    metric_statuses: nextStatuses,
    widgets_ready: ready,
    widgets_in_progress: inProgress,
    widgets_missing: missing,
    widgets_failed: failed,
    widgets_not_enough_data: notEnough,
    widgets_empty_genai_signal: 0,
  };
}
